import $api from "@/zapi-frontend/shared/api/axios";
import { AxiosError } from "axios";

export type TExportProjectOptions = {
  fileName: string;
  docType: "pdf" | "docx" | "md";
  reportType: "full" | "executive";
  includeDecisionFrame: boolean;
  includeProjectInstructions: boolean;
  includeAnalysisAndIntelligence: boolean;
  includeVault: boolean;
};

async function extractExportErrorMessage(error: unknown): Promise<string> {
  const data = (error as AxiosError)?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (typeof parsed?.message === "string" && parsed.message) return parsed.message;
    } catch {
      // error body wasn't JSON — fall back to the generic message
    }
  }
  return "Failed to export document";
}

export async function exportProject(projectId: number, options: TExportProjectOptions): Promise<void> {
  const fileName = options.fileName.trim() || "report";

  const params = new URLSearchParams({
    docType: options.docType,
    fileName,
    reportType: options.reportType,
    includeAnalysisQuestions: String(options.includeAnalysisAndIntelligence),
    includeIntelligenceQuestions: String(options.includeAnalysisAndIntelligence),
    includeDecisionFrame: String(options.includeDecisionFrame),
    includeProjectInstructions: String(options.includeProjectInstructions),
    includeVault: String(options.includeVault)
  });

  const response = await $api
    .get(`/projects/${projectId}/export?${params.toString()}`, { responseType: "blob" })
    .catch(async (error) => {
      throw new Error(await extractExportErrorMessage(error));
    });

  const contentType = (response.headers["content-type"] as string | undefined) || "";
  const isZip = contentType.includes("application/zip");
  const blob = new Blob([response.data], { type: contentType || undefined });

  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = isZip ? `${fileName}.zip` : `${fileName}.${options.docType}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
}
